/**
 * Take a screenshot at a chosen viewport without disturbing the one on screen.
 *
 * The automatic visual review needs a desktop shot and a mobile shot. It used
 * to get them by re-laying-out the frame the creator is looking at — desktop,
 * capture, mobile, capture, restore. The end state was correct, so a check on
 * the final value passed, and what the creator actually watched was the app
 * changing size on every page.
 *
 * How long a capture takes is not known — an attempt to measure it ran in a
 * hidden browser pane, where `requestAnimationFrame` does not fire, so the
 * number that produced said more about the pane than the capture. What IS
 * known is that the duration is not bounded by anything in the old design, so
 * the frame was not necessarily borrowed for an instant.
 *
 * Capturing a clone in an offscreen host varies exactly what the old approach
 * varied — the width of the containing box — so the rendering is the same,
 * while the creator's frame never moves.
 */

const DEFAULT_CAPTURE_TIMEOUT_MS = 20_000

export interface CaptureViewport {
  width: number
  height: number
}

export async function captureElementAtViewport(options: {
  element: HTMLElement
  viewport: CaptureViewport
  capture: (element: HTMLElement) => Promise<string>
  waitForPaint: () => Promise<void>
  timeoutMs?: number
}): Promise<string> {
  const doc = options.element.ownerDocument
  const host = doc.createElement('div')
  host.setAttribute('data-cogo-preview-capture-host', 'true')
  host.setAttribute('aria-hidden', 'true')
  host.style.position = 'fixed'
  host.style.left = '-100000px'
  host.style.top = '0'
  host.style.width = `${options.viewport.width}px`
  host.style.height = `${options.viewport.height}px`
  // Never a target for the creator's pointer or the tab order, even for the
  // frame it exists.
  host.style.pointerEvents = 'none'

  const clone = options.element.cloneNode(true) as HTMLElement
  host.append(clone)
  doc.body.append(host)

  let timer: ReturnType<typeof setTimeout> | null = null
  const budget = options.timeoutMs ?? DEFAULT_CAPTURE_TIMEOUT_MS
  // The budget covers the WHOLE attempt, paint included. It first wrapped only
  // the capture, and the browser showed why that is not enough: `waitForPaint`
  // waits on `requestAnimationFrame`, which a hidden tab does not fire, so the
  // attempt hung before the timer was ever created and the offscreen host was
  // still in the document a minute later. A creator switching tabs is enough
  // to reach that.
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Preview capture timed out after ${budget}ms`)),
      budget,
    )
  })
  try {
    return await Promise.race([
      (async () => {
        await options.waitForPaint()
        return options.capture(clone)
      })(),
      deadline,
    ])
  } finally {
    if (timer !== null) clearTimeout(timer)
    host.remove()
  }
}
